import { NormalizedCacheObject } from 'apollo-cache-inmemory';
import ApolloClient from 'apollo-client';
import gql from 'graphql-tag';

import {
  Category,
  Client,
  ClientProduct,
  FileUpload,
  Job,
  Outlet,
  Product,
  Project,
  Retailer,
  User,
  Task,
  Entry,
} from '@/core/entity';
import {
  ICategoryGateway,
  IClientGateway,
  IClientProductGateway,
  IEntryRepository,
  IJobGateway,
  IProductGateway,
  IProjectGateway,
  IRetailerGateway,
  ITaskGateway,
  IUserGateway,
  TaskInput,
  EntryInput,
} from '@/core/interface';
import {
  CategoryInput,
  ClientInput,
  JobInput,
  MapProductInput,
  OutletInput,
  ProductInput,
  ProjectInput,
  RetailerInput,
  UserInput,
} from '@/core/interface';
import { Fragment, UpdaterFunc } from '@/core/interface/common';
import {ReviewerEntryInput} from '@/core/interface/entry';

export class APIGateway implements
  ICategoryGateway,
  IClientGateway,
  IClientProductGateway,
  IEntryRepository,
  IJobGateway,
  IProductGateway,
  IProjectGateway,
  IRetailerGateway,
  ITaskGateway,
  IUserGateway {

  constructor(
    private readonly client: ApolloClient<NormalizedCacheObject>,
  ) { }

  public async approveEntries(
    ids: string[],
    fragment?: Fragment,
    updater?: UpdaterFunc,
  ): Promise<Entry[]> {
    const fragmentName = this.getFragmentName(fragment);
    const fragmentQuery = this.getFragmentQuery(fragment);

    const query = gql`
    mutation ($ids: [ID]!) {
      approveEntries(ids: $ids) {
        id

        ${fragmentName}
      }
    }
    ${fragmentQuery}
    `;

    const result: Entry[] = await this.mutation('approveEntries', query, { ids }, updater);

    return result;
  }

  public async assignCse(clientId: string, cseIds: string[], updater?: UpdaterFunc): Promise<Client> {
    const query = gql`
      mutation ($clientId: ID!, $cseIds: [ID!]!) {
        assignCSE(client: $clientId, cse: $cseIds) {
          id
          name
          country
          picName
          picEmail
          cse {
            id
            name
          }
        }
      }
    `;

    const result: Client = await this.mutation('assignCSE', query, { clientId, cseIds }, updater);

    return result;
  }

  public async assignTask(
    taskId: string,
    userId: string,
    fragment?: Fragment,
    updater?: UpdaterFunc,
  ): Promise<Task> {
    const fragmentName = this.getFragmentName(fragment);
    const fragmentQuery = this.getFragmentQuery(fragment);

    const query = gql`
      mutation ($taskId: ID!, $userId: ID!) {
        assignTask(task: $taskId, auditor: $userId) {
          id
          ${fragmentName}
        }
      }
      ${fragmentQuery}
    `;

    const result: Client = await this.mutation('assignTask', query, { taskId, userId }, updater);

    return result;
  }

  public async createCategory(input: CategoryInput, updater: UpdaterFunc): Promise<Category> {
    const query = gql`
    mutation ($input: CategoryInput!) {
      newCategory(input: $input) {
        id
        name
      }
    }
    `;

    const result: Category = await this.mutation('newCategory', query, { input }, updater);

    return result;
  }

  public async createClient(input: ClientInput, fragment?: Fragment, updater?: UpdaterFunc): Promise<Client> {
    const fragmentName = this.getFragmentName(fragment);
    const fragmentQuery = this.getFragmentQuery(fragment);

    const query = gql`
      mutation ($input: ClientInput!) {
        newClient(input: $input) {
          id
          name
          country
          picName
          picEmail
          ${fragmentName}
        }
      }
      ${fragmentQuery}
    `;

    const result: Client = await this.mutation('newClient', query, { input }, updater);

    return result;
  }

  public async createJob(input: JobInput, fragment?: Fragment, updater?: UpdaterFunc): Promise<Job> {
    const fragmentName = this.getFragmentName(fragment);
    const fragmentQuery = this.getFragmentQuery(fragment);

    const query = gql`
      mutation ($input: JobInput!) {
        newJob(input: $input) {
          id
          ${fragmentName}
        }
      }
      ${fragmentQuery}
    `;

    const result: Job = await this.mutation('newJob', query, { input }, updater);

    return result;
  }

  public async createOutlet(input: OutletInput, fragment?: Fragment, updater?: UpdaterFunc): Promise<Outlet> {
    const fragmentName = this.getFragmentName(fragment);
    const fragmentQuery = this.getFragmentQuery(fragment);

    const query = gql`
      mutation ($input: OutletInput!) {
        newOutlet(input: $input) {
          id
          name
          address
          lat
          lon
          ${fragmentName}
        }
      }
      ${fragmentQuery}
    `;

    const result: Outlet = await this.mutation('newOutlet', query, { input }, updater);

    return result;
  }

  public async createProduct(input: ProductInput, fragment?: Fragment, updater?: UpdaterFunc): Promise<Product> {
    const fragmentName = this.getFragmentName(fragment);
    const fragmentQuery = this.getFragmentQuery(fragment);

    const query = gql`
      mutation ($input: ProductInput!) {
        newProduct(input: $input) {
          id
          ${fragmentName}
        }
      }
      ${fragmentQuery}
    `;

    const result: Product = await this.mutation('newProduct', query, { input }, updater);

    return result;
  }

  public async createProject(input: ProjectInput, fragment?: Fragment, updater?: UpdaterFunc): Promise<Project> {
    const fragmentName = this.getFragmentName(fragment);
    const fragmentQuery = this.getFragmentQuery(fragment);

    const query = gql`
      mutation ($input: ProjectInput!) {
        newProject(input: $input) {
          id
          ${fragmentName}
        }
      }
      ${fragmentQuery}
    `;

    const result: Project = await this.mutation('newProject', query, { input }, updater);

    return result;
  }

  public async createRetailer(
    input: RetailerInput,
    fragment?: Fragment,
    updater?: UpdaterFunc,
  ): Promise<Retailer> {
    const fragmentName = this.getFragmentName(fragment);
    const fragmentQuery = this.getFragmentQuery(fragment);

    const query = gql`
      mutation ($name: String!, $logo: String!, $country: String!, $desId: ID!) {
        newRetailer(name: $name, logo: $logo, country: $country, desId: $desId) {
          id
          name
          logo(width: 200, height: 70) {
            id
            url
          }
          country
          desId
          ${fragmentName}
        }
      }
      ${fragmentQuery}
    `;


    const result: Retailer = await this.mutation('newRetailer', query, input, updater);

    return result;
  }

  public async createUser(
    input: UserInput,
    fragment?: Fragment,
    updater?: UpdaterFunc,
  ): Promise<User> {
    const fragmentName = this.getFragmentName(fragment);
    const fragmentQuery = this.getFragmentQuery(fragment);

    const query = gql`
      mutation ($input: UserInput!) {
        newUser(input: $input) {
          id
          ${fragmentName}
        }
      }
      ${fragmentQuery}
    `;

    const result: User = await this.mutation('newUser', query, { input }, updater);

    return result;
  }

  public async getJobCsvUploadUrl(): Promise<FileUpload> {
    const query = gql`
    mutation {
      getUploadJobCsvUrl {
        id
        fileUri
        uploadUrl
      }
    }
    `;

    const result: FileUpload = await this.mutation('getUploadJobCsvUrl', query, undefined);

    return result;
  }

  public async getJobImages(
      id: string,
      fromEmail: string
  ): Promise<boolean> {
    const query = gql`
        mutation ($id: ID!, $fromEmail: String!) {
            generateImageZip(id: $id, from_email: $fromEmail)
        }
    `;
    const result: boolean = await this.mutation('generateImageZip', query, { id, fromEmail });

    return result;
  }

  public async getRetailerLogoUploadUrl(mimeType: string): Promise<FileUpload> {
    const query = gql`
    mutation ($mimeType: String!) {
      getRetailerLogoUploadUrl(mimeType: $mimeType) {
        id
        fileUri
        uploadUrl
      }
    }`;

    const result: FileUpload = await this.mutation('getRetailerLogoUploadUrl', query, { mimeType });

    return result;
  }

  public async getUserAvatarUploadUrl(mimeType: string): Promise<FileUpload> {
    const query = gql`
    mutation ($mimeType: String!) {
      getUserAvatarUploadUrl(mimeType: $mimeType) {
        id
        fileUri
        uploadUrl
      }
    }`;

    const result: FileUpload = await this.mutation('getUserAvatarUploadUrl', query, { mimeType });

    return result;
  }

  public async mapProduct(
    input: MapProductInput,
    fragment?: Fragment | undefined,
    updater?: UpdaterFunc | undefined,
  ): Promise<ClientProduct> {
    const fragmentName = this.getFragmentName(fragment);
    const fragmentQuery = this.getFragmentQuery(fragment);

    const query = gql`
    mutation ($client: ID!, $product: ID!, $intrack: ID!) {
      mapProduct(client: $client, product: $product, intrack: $intrack) {
        id
        ${fragmentName}
      }
    }
    ${fragmentQuery}
    `;

    const result: ClientProduct = await this.mutation('mapProduct', query, {
      client: input.clientId,
      product: input.clientProductId,
      intrack: input.intrackProductId,
    }, updater);

    return result;
  }

  public async newTasks(country: string, date: string, fragment?: Fragment, updater?: UpdaterFunc): Promise<Task[]> {
    const fragmentName = this.getFragmentName(fragment);
    const fragmentQuery = this.getFragmentQuery(fragment);

    const query = gql`
    mutation ($date: String!, $country: String!) {
      newTasks(date: $date, country: $country) {
        id
        ${fragmentName}
      }
    }
    ${fragmentQuery}
    `;

    const result: Task[] = await this.mutation('newTasks', query, { date, country }, updater);

    return result;
  }

  public async removeCategory(id: string, updater?: UpdaterFunc | undefined): Promise<Category> {
    const query = gql`
    mutation ($id: ID!) {
      removeCategory(id: $id) {
        id
        name
      }
    }
    `;

    const result: Category = await this.mutation('removeCategory', query, { id }, updater);

    return result;
  }

  public async removeJob(
    id: string,
    fragment?: Fragment,
    updater?: UpdaterFunc,
    refetchQueries?: string[],
  ): Promise<Job> {
    const fragmentName = fragment ? `...${fragment.name}` : '';
    const fragmentQuery = fragment ? fragment.query : '';

    const query = gql`
      mutation ($id: ID!) {
        removeJob(id: $id) {
          id
          ${fragmentName}
        }
      }
      ${fragmentQuery}
    `;

    const result: Job = await this.mutation('removeJob', query, { id }, updater, refetchQueries);

    return result;
  }

  public async removeProject(
    id: string,
    fragment?: Fragment,
    updater?: UpdaterFunc,
    refetchQueries?: string[],
  ): Promise<Project> {
    const fragmentName = fragment ? `...${fragment.name}` : '';
    const fragmentQuery = fragment ? fragment.query : '';

    const query = gql`
      mutation ($id: ID!) {
        removeProject(id: $id) {
          id
          ${fragmentName}
        }
      }
      ${fragmentQuery}
    `;

    const result: Project = await this.mutation('removeProject', query, { id }, updater, refetchQueries);

    return result;
  }

  public async removeRetailer(
    id: string,
    fragment?: Fragment,
    updater?: UpdaterFunc,
  ): Promise<Retailer> {
    const fragmentName = fragment ? `...${fragment.name}` : '';
    const fragmentQuery = fragment ? fragment.query : '';

    const query = gql`
      mutation ($id: ID!) {
        removeRetailer(id: $id) {
          id
          ${fragmentName}
        }
      }
      ${fragmentQuery}
    `;

    const result: Retailer = await this.mutation('removeRetailer', query, { id }, updater);

    return result;
  }

  public async splitTask(
    task: string,
    entries: string[],
    fragment?: Fragment,
    updater?: UpdaterFunc,
  ): Promise<Task> {
    const fragmentName = fragment ? `...${fragment.name}` : '';
    const fragmentQuery = fragment ? fragment.query : '';

    const query = gql`
    mutation ($task: ID!, $entries:[ID!]!) {
      splitTask(task: $task, entries: $entries) {
        id
        ${fragmentName}
      }
    }
    ${fragmentQuery}
  `;

    const result: Task = await this.mutation('splitTask', query, { task, entries }, updater);

    return result;
  }

  public async updateCategory(id: string, input: CategoryInput, updater: UpdaterFunc): Promise<Category> {
    const query = gql`
    mutation ($id: ID!, $input: CategoryInput!) {
      updateCategory(id: $id, input: $input) {
        id
        name
      }
    }
    `;

    const result: Category = await this.mutation('updateCategory', query, { id, input }, updater);

    return result;
  }

  public async updateClient(
    id: string,
    input: ClientInput,
    fragment?: Fragment,
    updater?: UpdaterFunc,
  ): Promise<Client> {
    const fragmentName = this.getFragmentName(fragment);
    const fragmentQuery = this.getFragmentQuery(fragment);

    const query = gql`
     mutation ($id: ID!, $input: ClientInput!) {
        updateClient(id: $id, input: $input) {
          id
          name
          country
          picName
          picEmail
          ${fragmentName}
        }
      }
      ${fragmentQuery}
    `;

    const result: Client = await this.mutation('updateClient', query, { id, input }, updater);

    return result;
  }

  public async updateEntry(
    input: EntryInput | ReviewerEntryInput,
    fragment?: Fragment,
    updater?: UpdaterFunc,
  ): Promise<Entry> {
    const fragmentName = this.getFragmentName(fragment);
    const fragmentQuery = this.getFragmentQuery(fragment);

    const query = gql`
    mutation ($input: [EntryInput!]!) {
        updateEntriesAsReviewer(input: $input) {
        id
        currPrice
        ${fragmentName}
      }
    }
    ${fragmentQuery}
    `;

    const result: Entry[] = await this.mutation('updateEntriesAsReviewer', query, {
      input: [{
        ...input,
        // the following properties are required by EntryInput but is a no-op
        // when performed by a reviewer
        attachments: [],
        isNa: false,
        isOos: false,
        isPromo: false,
        modifiedAt: 0,
        note: '',
      }]
    }, updater);

    if (result && result.length === 1) {
      return result[0];
    } else {
      throw new Error('Unable to update price.');
    }
  }

  public async reviewerUpdate(
      input: ReviewerEntryInput,
      fragment?: Fragment,
      updater?: UpdaterFunc,
  ): Promise<Entry> {
    const fragmentName = this.getFragmentName(fragment);
    const fragmentQuery = this.getFragmentQuery(fragment);

    const query = gql`
        mutation ($input: [EntryInput!]!) {
            updateEntriesAsReviewer(input: $input) {
                id
                currPrice
                ${fragmentName}
            }
        }
        ${fragmentQuery}
    `;

    const result: Entry[] = await this.mutation('updateEntriesAsReviewer', query, {
      input: [{
        ...input,
        // the following properties are required by EntryInput but is a no-op
        // when performed by a reviewer
        attachments: [],
        isNa: false,
        modifiedAt: 0,
        note: '',
      }]
    }, updater);

    if (result && result.length === 1) {
      return result[0];
    } else {
      throw new Error('Unable to update price.');
    }
  }

  public async updateOutlet(
    id: string,
    input: OutletInput,
    fragment?: Fragment,
    updater?: UpdaterFunc,
  ): Promise<Outlet> {
    const fragmentName = this.getFragmentName(fragment);
    const fragmentQuery = this.getFragmentQuery(fragment);

    const query = gql`
      mutation ($id: ID!, $input: OutletInput!) {
        updateOutlet(id: $id, input: $input) {
          id
          name
          address
          lat
          lon
          ${fragmentName}
        }
      }
      ${fragmentQuery}
    `;

    const result: Outlet = await this.mutation('updateOutlet', query, { id, input }, updater);

    return result;
  }

  public async updateProduct(
    id: string, input: ProductInput, fragment?: Fragment, updater?: UpdaterFunc,
  ): Promise<Product> {
    const fragmentName = this.getFragmentName(fragment);
    const fragmentQuery = this.getFragmentQuery(fragment);

    const query = gql`
      mutation ($id: ID!, $input: ProductInput!) {
        updateProduct(id: $id, input: $input) {
          id
          ${fragmentName}
        }
      }
      ${fragmentQuery}
    `;

    const result: Project = await this.mutation('updateProduct', query, { id, input }, updater);

    return result;
  }

  public async updateProject(
    id: string,
    input: ProjectInput,
    fragment?: Fragment,
    updater?: UpdaterFunc,
  ): Promise<Project> {
    const fragmentName = this.getFragmentName(fragment);
    const fragmentQuery = this.getFragmentQuery(fragment);

    const query = gql`
      mutation ($id: ID!, $input: ProjectInput!) {
        updateProject(id: $id, input: $input) {
          id
          ${fragmentName}
        }
      }
      ${fragmentQuery}
    `;

    const result: Project = await this.mutation('updateProject', query, { id, input }, updater);

    return result;
  }

  public async updateRetailer(
    input: RetailerInput,
    fragment?: Fragment,
    updater?: UpdaterFunc,
  ): Promise<Retailer> {
    const fragmentName = this.getFragmentName(fragment);
    const fragmentQuery = this.getFragmentQuery(fragment);

    const query = gql`
      mutation ($id: ID!, $name: String!, $logo: String!, $country: String!, $desId: ID!) {
        updateRetailer(id: $id, name: $name, logo: $logo, country: $country, desId: $desId) {
          id
          name
          logo(width: 200, height: 70) {
            id
            url
          }
          country
          desId
          ${fragmentName}
        }
      }
      ${fragmentQuery}
    `;

    const result: Retailer = await this.mutation('updateRetailer', query, input, updater);

    return result;
  }

  public async updateTask(
    id: string,
    input: TaskInput,
    fragment?: Fragment,
    updater?: UpdaterFunc,
  ): Promise<Task> {
    const fragmentName = this.getFragmentName(fragment);
    const fragmentQuery = this.getFragmentQuery(fragment);

    const query = gql`
      mutation ($id: ID!, $input: TaskInput!) {
        updateTask(id: $id, input: $input) {
          id
          ${fragmentName}
        }
      }
      ${fragmentQuery}
    `;

    const result: Task = await this.mutation('updateTask', query, { id, input }, updater);

    return result;
  }

  public async updateUser(
    id: string,
    input: UserInput,
    fragment?: Fragment,
    updater?: UpdaterFunc,
  ): Promise<User> {
    const fragmentName = this.getFragmentName(fragment);
    const fragmentQuery = this.getFragmentQuery(fragment);

    const query = gql`
      mutation ($id: ID!, $input: UserInput!) {
        updateUser(id: $id, input: $input) {
          id
          ${fragmentName}
        }
      }
      ${fragmentQuery}
    `;

    const result: User = await this.mutation('updateUser', query, { id, input }, updater);

    return result;
  }

  private getFragmentName(fragment?: Fragment | undefined) {
    return fragment ? `...${fragment.name}` : '';
  }

  private getFragmentQuery(fragment?: Fragment | undefined) {
    return fragment ? fragment.query : '';
  }

  private async mutation(
    queryName: string,
    query: any,
    input: any,
    updater?: UpdaterFunc,
    refetchQueries?: string[],
  ) {
    const result = await this.client.mutate({
      mutation: query,
      variables: input,
      update: updater,
      refetchQueries,
    });

    if (result.errors) {
      console.error(result.errors); // tslint:disable-line no-console
      throw result.errors[0];
    }

    return result.data[queryName];
  }
}
